import React, { useEffect, useState, useCallback } from "react";
import { MessageSquare, Check, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { agencyApi, apiErr } from "./agencyApi";
import { StatusPill, when } from "./AgencyBits";
import { computeGroupDescriptions, effectiveLineDescription } from "./lineDescriptions";

function money(amount, currency) {
  if (amount === null || amount === undefined) return "—";
  const n = Number(amount);
  const s = n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return currency ? `${currency} ${s}` : s;
}

// The agency-facing Cost Sheet (Screen 08). Renders ONLY the partner-safe projection served by
// GET /agency/projects/{pid}/quote — client-facing qty + unit rate + amount (internal cost/margin
// never reach the client). Read-only on pricing; the agency negotiates via per-line + general
// comments, Request Changes and Approve.
export default function AgencyCostSheet({ projectId, projectName }) {
  const [data, setData] = useState(null);
  const [comments, setComments] = useState([]);
  const [status, setStatus] = useState(null);
  const [versions, setVersions] = useState([]);
  const [err, setErr] = useState("");
  const [actionErr, setActionErr] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    // Perf fix (2026-10, Cursor audit item 7 / group 0B): quote/comments/versions are 3
    // independent reads (versions never depended on quote or comments) that were fetched one
    // after another; now fired together. Same error precedence as before — a quote failure
    // (or, failing that, a comments failure) sets `err`; a versions failure stays silent, same
    // as its own try/catch always did. No change to the partner-safe quote projection itself.
    const [quoteResult, commentsResult, versionsResult] = await Promise.allSettled([
      agencyApi.quote(projectId),
      agencyApi.costSheetComments(projectId),
      agencyApi.costSheetVersions(projectId),
    ]);
    if (quoteResult.status === "fulfilled") setData(quoteResult.value.data);
    if (commentsResult.status === "fulfilled") {
      setComments(commentsResult.value.data.comments || []);
      setStatus(commentsResult.value.data.status);
    }
    if (versionsResult.status === "fulfilled") setVersions(versionsResult.value.data.versions || []);
    if (quoteResult.status === "rejected") setErr(apiErr(quoteResult.reason));
    else if (commentsResult.status === "rejected") setErr(apiErr(commentsResult.reason));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const postComment = async (text, lineId) => {
    setActionErr("");
    try {
      await agencyApi.addCostSheetComment(projectId, text, lineId);
      const c = await agencyApi.costSheetComments(projectId);
      setComments(c.data.comments || []);
      const r = await agencyApi.quote(projectId); setData(r.data);
    } catch (e) { setActionErr(apiErr(e)); throw e; }
  };

  const approve = async () => {
    setBusy("approve");
    try { await agencyApi.approveCostSheet(projectId); await load(); } catch (e) { setErr(apiErr(e)); } finally { setBusy(""); }
  };

  if (err) return <div className="ag-empty" data-testid="agency-cost-sheet-error">{err}</div>;
  if (!data) {
    // Part B (2026-10), item 5 — a real skeleton instead of a single line of text, same pulse
    // animation AgencyBtsGallery's own loading state already uses (`.ag-skeleton`).
    return (
      <div className="ag-stack" data-testid="agency-cost-sheet-loading">
        <div className="ag-skeleton" style={{ height: 28, width: "40%", borderRadius: 6 }} />
        <div className="ag-card">
          {[0, 1, 2, 3].map((i) => <div key={i} className="ag-skeleton" style={{ height: 18, marginBottom: 12, borderRadius: 4 }} />)}
        </div>
      </div>
    );
  }
  if (!data.has_cost_sheet || !data.cost_sheet) {
    return <div className="ag-empty" data-testid="agency-cost-sheet-empty">Your NJM producer shares the cost sheet here once it's ready.</div>;
  }

  const cs = data.cost_sheet;
  const cur = cs.currency || cs.totals?.currency;
  const diff = data.diff || {};
  const lineChanges = diff.line_changes || {};
  const removed = diff.removed_lines || [];
  const counts = data.comment_counts || {};
  const version = data.version || cs.version || 1;
  const effStatus = status || cs.status || "Shared";
  const isApproved = effStatus === "Approved";
  const isChangesReq = effStatus === "Changes requested";

  const changed = Object.values(lineChanges).filter((v) => v === "changed").length;
  const added = Object.values(lineChanges).filter((v) => v === "added").length;
  const removedCount = removed.length;
  const totalChanges = changed + added + removedCount;
  const showChangeBanner = !diff.baseline && totalChanges > 0 && version > 1 && !isApproved;

  const commentsFor = (lid) => comments.filter((c) => (c.line_id || null) === (lid || null));
  const generalComments = comments.filter((c) => !c.line_id);

  // Prefer the presentation groups (per-shoot/unit) when NJM has grouped; else canonical sections.
  const blocks = cs.groups && cs.groups.length
    ? cs.groups.map((g) => ({ title: g.name, subtotal: g.subtotal, subgroups: g.subgroups }))
    : (cs.sections || []).map((s) => ({ title: s.name, subtotal: s.subtotal, subgroups: [{ name: null, lines: s.lines, subtotal: s.subtotal }] }));

  const blockTitles = new Set(blocks.map((b) => b.title));
  const removedFor = (title) => removed.filter((r) => (r.display_group || r.section) === title);
  const removedOrphans = removed.filter((r) => !blockTitles.has(r.display_group || r.section));

  // Line sub-text fix (2026-11) — see lineDescriptions.js. Grouped by the finest heading
  // already shown above each line (subgroup name, else the block title) so a Client
  // Description shared by 2+ lines in that same visual cluster is suppressed per-line.
  const _descHeading = (l) => l._descGroupKey;
  const _descLines = blocks.flatMap((b) => b.subgroups.flatMap((sg) => (sg.lines || []).map((l) => ({ ...l, _descGroupKey: sg.name || b.title }))));
  const descGroupDesc = computeGroupDescriptions(_descLines, _descHeading, (l) => l.description);

  const RemovedLine = ({ r }) => (
    <div key={r.line_id} className="ag-cs-row ag-cs-line ag-cs-removed" data-testid="agency-cost-sheet-removed-line">
      <div><span className="ag-cs-desc" style={{ textDecoration: "line-through", color: "var(--faint)" }}>{r.display_name}</span>
        <span className="ag-cs-chip ag-cs-chip-removed">Removed in V{version}</span></div>
      <div /><div />
      <div className="ag-cs-num" style={{ textDecoration: "line-through", color: "var(--faint)" }}>{money(r.amount, cur)}</div>
      <div />
    </div>
  );

  const t = cs.totals || {};

  return (
    <div data-testid="agency-cost-sheet">
      {actionErr && <div className="ag-alert ag-alert-err" role="alert" data-testid="agency-cost-sheet-action-error" style={{ marginBottom: 16 }}>{actionErr}</div>}
      {/* header */}
      <div className="ag-cs-head">
        <div>
          <h2 className="ag-h2" style={{ marginBottom: 10 }}>{cs.quote_name || "Cost sheet"}</h2>
          <div className="ag-cs-meta">
            {projectName && <span>{projectName}</span>}
            <span>Currency <strong>{cur}</strong></span>
            <span>Version <strong>V{version}</strong></span>
            {cs.updated_at && <span>Issued {when(cs.updated_at)}</span>}
          </div>
        </div>
        <div className="ag-cs-statusbox" data-testid="agency-cost-sheet-status">
          <div className="ag-label" style={{ marginBottom: 8 }}>Status</div>
          <StatusPill status={effStatus} />
          <div className="ag-cs-gross">
            <div className="ag-label" style={{ marginBottom: 4 }}>Gross total</div>
            <div className="ag-cs-gross-amt">{money(t.grand_total, cur)}</div>
          </div>
        </div>
      </div>

      {/* change banner */}
      {showChangeBanner && (
        <div className="ag-cs-banner ag-cs-banner-dark" data-testid="agency-cost-sheet-change-banner">
          <div className="ag-cs-banner-title">{totalChanges} change{totalChanges > 1 ? "s" : ""} since V{version - 1}</div>
          <div className="ag-cs-banner-body">
            {changed > 0 && <span>{changed} price{changed > 1 ? "s" : ""} revised</span>}
            {added > 0 && <span>{added} line{added > 1 ? "s" : ""} added</span>}
            {removedCount > 0 && <span>{removedCount} line{removedCount > 1 ? "s" : ""} removed</span>}
            {typeof diff.total_delta === "number" && diff.total_delta !== 0 && (
              <span className="ag-cs-delta">{diff.total_delta > 0 ? "+" : "−"}{money(Math.abs(diff.total_delta), cur)} to total</span>
            )}
          </div>
        </div>
      )}
      {isChangesReq && (
        <div className="ag-cs-banner ag-cs-banner-amber" data-testid="agency-cost-sheet-changes-banner">
          <AlertCircle size={16} style={{ flex: "none" }} />
          <div>You've requested changes. NJM will review your notes and re-issue the cost sheet.</div>
        </div>
      )}

      <div className="ag-cs-layout">
        {/* left — breakdown */}
        <div>
          <div className="ag-card" style={{ padding: 0 }}>
            <div className="ag-cs-row ag-cs-header-row">
              <div>Description</div>
              <div className="ag-cs-num">Qty</div>
              <div className="ag-cs-num">Rate</div>
              <div className="ag-cs-num">Amount</div>
              <div />
            </div>
            {blocks.map((b, bi) => (
              <div key={bi} className="ag-cs-group" data-testid="agency-cost-sheet-group">
                <div className="ag-cs-group-title">{b.title}</div>
                {b.subgroups.map((sg, si) => (
                  <div key={si}>
                    {sg.name && <div className="ag-cs-subgroup-title">{sg.name}</div>}
                    {(sg.lines || []).map((l) => (
                      <LineRow key={l.line_id} line={l} currency={cur} change={lineChanges[l.line_id]} version={version}
                        count={counts[l.line_id] || 0} thread={commentsFor(l.line_id)} onPost={postComment} disabled={isApproved}
                        effectiveDescription={effectiveLineDescription({ ...l, _descGroupKey: sg.name || b.title }, _descHeading, (x) => x.description, descGroupDesc)} />
                    ))}
                    <div className="ag-cs-row ag-cs-subtotal-row">
                      <div>{sg.name ? `${sg.name} subtotal` : `${b.title} subtotal`}</div>
                      <div /><div />
                      <div className="ag-cs-num">{money(sg.subtotal, cur)}</div>
                      <div />
                    </div>
                  </div>
                ))}
                {removedFor(b.title).map((r) => <RemovedLine key={r.line_id} r={r} />)}
              </div>
            ))}
            {removedOrphans.map((r) => <RemovedLine key={r.line_id} r={r} />)}
            {/* totals */}
            <div className="ag-cs-totals" data-testid="agency-cost-sheet-totals">
              <div className="ag-cs-total-line"><span>Subtotal</span><span>{money(t.subtotal, cur)}</span></div>
              {t.gst ? <div className="ag-cs-total-line"><span>GST</span><span>{money(t.gst, cur)}</span></div> : null}
              <div className="ag-cs-total-line ag-cs-grand"><span>Grand total</span><span>{money(t.grand_total, cur)}</span></div>
              {t.advance !== null && t.advance !== undefined && (
                <>
                  <div className="ag-cs-total-line" style={{ marginTop: 8 }}><span>Advance</span><span>{money(t.advance, cur)}</span></div>
                  <div className="ag-cs-total-line"><span>Pending</span><span>{money(t.pending, cur)}</span></div>
                </>
              )}
            </div>
          </div>

          {/* general comments */}
          <div className="ag-card" style={{ marginTop: 18 }}>
            <h3 className="ag-eyebrow" style={{ marginBottom: 14 }}>Notes to NJM</h3>
            {generalComments.length === 0 && <div className="ag-small" style={{ marginBottom: 12 }}>No general notes yet.</div>}
            <div className="ag-stack" style={{ marginBottom: generalComments.length ? 16 : 0 }}>
              {generalComments.map((c) => <CommentBubble key={c.id} c={c} />)}
            </div>
            <CommentInput onPost={(txt) => postComment(txt, null)} placeholder="Add a note for NJM about this cost sheet…" testid="agency-general-comment" disabled={isApproved} />
          </div>
        </div>

        {/* right — actions + version history */}
        <div className="ag-cs-side">
          <div className="ag-card">
            <div className="ag-label" style={{ marginBottom: 4 }}>This version</div>
            <div className="ag-h3" style={{ fontSize: 22, marginBottom: 2 }}>V{version}</div>
            <div className="ag-small" style={{ marginBottom: 18 }}>{cs.updated_at ? `Issued ${when(cs.updated_at)}` : ""}</div>
            {isApproved ? (
              <div className="ag-cs-approved" data-testid="agency-cost-sheet-approved-badge"><Check size={16} /> Approved</div>
            ) : isChangesReq ? (
              <div data-testid="agency-cost-sheet-changes-state">
                <div className="ag-cs-approved" style={{ background: "var(--cream)", color: "var(--gold-ink)", marginBottom: 12 }}><AlertCircle size={15} /> Changes requested</div>
                <div className="ag-small" style={{ marginBottom: 12 }}>NJM has been notified and will re-issue a revised cost sheet. You can still approve the current version as-is.</div>
                <button className="ag-btn ag-btn-gold" style={{ width: "100%" }} onClick={approve} disabled={busy === "approve"} data-testid="agency-approve-cost-sheet">
                  {busy === "approve" ? "Approving…" : "Approve current version"}
                </button>
              </div>
            ) : (
              <div className="ag-stack">
                <button className="ag-btn ag-btn-gold" style={{ width: "100%" }} onClick={approve} disabled={busy === "approve"} data-testid="agency-approve-cost-sheet">
                  {busy === "approve" ? "Approving…" : "Approve cost sheet"}
                </button>
                <RequestChanges projectId={projectId} onDone={load} onErr={setActionErr} disabled={busy !== ""} />
              </div>
            )}
          </div>

          {versions.length > 1 && (
            <div className="ag-card" style={{ marginTop: 18 }} data-testid="agency-cost-sheet-history">
              <h3 className="ag-eyebrow" style={{ marginBottom: 14 }}>Version history</h3>
              <div className="ag-stack">
                {[...versions].reverse().map((v) => (
                  <div key={v.version} className="ag-cs-hist-row">
                    <div>
                      <div className="ag-body" style={{ fontWeight: 600, fontSize: 13 }}>V{v.version} {v.version === version && <span className="ag-cs-chip ag-cs-chip-current">Current</span>}</div>
                      <div className="ag-small">{v.issued_at ? when(v.issued_at) : ""}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div className="ag-body" style={{ fontSize: 13 }}>{money(v.grand_total, v.currency || cur)}</div>
                      {typeof v.total_delta === "number" && v.total_delta !== 0 && <div className="ag-small" style={{ color: v.total_delta > 0 ? "var(--red)" : "var(--green)" }}>{v.total_delta > 0 ? "+" : "−"}{money(Math.abs(v.total_delta), v.currency || cur)}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LineRow({ line, currency, change, version, count, thread, onPost, disabled, effectiveDescription }) {
  const [open, setOpen] = useState(false);
  const chip = change === "changed" ? <span className="ag-cs-chip ag-cs-chip-changed">Changed in V{version}</span>
    : change === "added" ? <span className="ag-cs-chip ag-cs-chip-added">New in V{version}</span> : null;
  return (
    <div className="ag-cs-linewrap" data-testid="agency-cost-sheet-line">
      <div className={`ag-cs-row ag-cs-line${change === "changed" || change === "added" ? " ag-cs-line-hl" : ""}`}>
        <div>
          <span className="ag-cs-desc">{line.display_name}</span>
          {chip}
          {line.rate_missing && <span className="ag-cs-chip ag-cs-chip-removed">Rate TBC</span>}
          {effectiveDescription && <div className="ag-small" style={{ marginTop: 3 }}>{effectiveDescription}</div>}
        </div>
        <div className="ag-cs-num">{line.qty !== null && line.qty !== undefined ? line.qty : "—"}</div>
        <div className="ag-cs-num">{line.client_rate !== null && line.client_rate !== undefined ? money(line.client_rate, currency) : "—"}</div>
        <div className="ag-cs-num ag-cs-amt">{money(line.amount, currency)}</div>
        <div>
          <button className="ag-cs-cbtn" onClick={() => setOpen((o) => !o)} data-testid="agency-line-comment-toggle" aria-label="Comments">
            <MessageSquare size={15} />
            {count > 0 && <span className="ag-cs-cbadge">{count}</span>}
          </button>
        </div>
      </div>
      {open && (
        <div className="ag-cs-thread" data-testid="agency-line-thread">
          {thread.length === 0 && <div className="ag-small" style={{ marginBottom: 10 }}>No comments on this line yet.</div>}
          <div className="ag-stack" style={{ marginBottom: thread.length ? 12 : 0 }}>
            {thread.map((c) => <CommentBubble key={c.id} c={c} />)}
          </div>
          {!disabled && <CommentInput onPost={(txt) => onPost(txt, line.line_id)} placeholder="Ask about this line…" testid="agency-line-comment" small />}
        </div>
      )}
    </div>
  );
}

function CommentBubble({ c }) {
  const mine = c.author_type === "partner";
  return (
    <div className="ag-cs-comment">
      <div className={`ag-cs-comment-av${mine ? " mine" : ""}`}>{mine ? "You" : "NJM"}</div>
      <div style={{ flex: 1 }}>
        <div className="ag-body" style={{ fontSize: 12.5 }}>{c.text}</div>
        <div className="ag-small" style={{ color: "var(--faint)", marginTop: 2 }}>{c.author_type === "partner" ? "You" : "NJM"} · {when(c.at)}</div>
      </div>
    </div>
  );
}

function CommentInput({ onPost, placeholder, testid, small, disabled }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try { await onPost(text.trim()); setText(""); } finally { setBusy(false); }
  };
  if (disabled) return null;
  return (
    <div className="ag-cs-cinput">
      <textarea className="ag-input" rows={small ? 2 : 2} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} data-testid={`${testid}-input`} />
      <button className="ag-btn ag-btn-dark" style={{ marginTop: 8 }} onClick={submit} disabled={busy || !text.trim()} data-testid={`${testid}-submit`}>{busy ? "Posting…" : "Post comment"}</button>
    </div>
  );
}

function RequestChanges({ projectId, onDone, onErr, disabled }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await agencyApi.requestCostSheetChanges(projectId, note.trim()); setOpen(false); setNote(""); onDone(); }
    catch (e) { onErr && onErr(apiErr(e)); }
    finally { setBusy(false); }
  };
  if (!open) return <button className="ag-btn ag-btn-ghost" style={{ width: "100%" }} onClick={() => setOpen(true)} disabled={disabled} data-testid="agency-request-changes-open"><span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>Request changes {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span></button>;
  return (
    <div style={{ marginTop: 4 }}>
      <textarea className="ag-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What would you like NJM to revise? (optional)" data-testid="agency-request-changes-note" />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button className="ag-btn ag-btn-dark" style={{ flex: 1 }} onClick={submit} disabled={busy} data-testid="agency-request-changes-submit">{busy ? "Sending…" : "Send request"}</button>
        <button className="ag-btn ag-btn-ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
}
